import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Reveal, { CountUp } from '../../components/Reveal';
import SiteBanner from '../../components/SiteBanner';
import Icon from '../../components/Icon';
import { useAuth, HOME_FOR_ROLE } from '../../context/AuthContext';
import usePageTitle from '../../hooks/usePageTitle';
import homeFeature from '../../assets/images/home-feature.jpg';

export default function Home() {
  const { t } = useTranslation();
  const { user } = useAuth();
  usePageTitle(t('home.pageTitle'));

  const promises = t('home.promises', { returnObjects: true });
  const services = t('home.services', { returnObjects: true });
  const steps = t('home.steps', { returnObjects: true });

  return (
    <>
      <SiteBanner />

      <section className="hero">
        <div className="hero-glow" aria-hidden="true" />
        <div className="hero-inner">
          <Reveal className="hero-copy">
            <span className="eyebrow">{t('home.eyebrow')}</span>
            <h1>
              {t('home.titleLine1')}
              <span className="accent-word"> {t('home.titleAccent')}</span>
            </h1>
            <p className="lede">{t('home.lede')}</p>
            <div className="hero-actions">
              {user ? (
                <Link className="btn btn-primary btn-lg" to={HOME_FOR_ROLE[user.role] || '/'}>
                  {t('home.goToPortal')}
                </Link>
              ) : (
                <>
                  <Link className="btn btn-primary btn-lg" to="/register/family">{t('home.iNeedNanny')}</Link>
                  <Link className="btn btn-outline btn-lg" to="/register/nanny">{t('home.iWantToWork')}</Link>
                </>
              )}
            </div>
            <dl className="hero-stats">
              <div><dt><CountUp to={24} suffix="h" /></dt><dd>{t('home.statResponse')}</dd></div>
              <div><dt><CountUp to={100} suffix="%" /></dt><dd>{t('home.statDocuments')}</dd></div>
              <div><dt><CountUp to={7} /></dt><dd>{t('home.statDays')}</dd></div>
            </dl>
          </Reveal>

          <Reveal className="hero-card-wrap" delay={120}>
            <div className="request-card">
              <header>
                <span className="request-dot" aria-hidden="true" />
                <div>
                  <strong>{t('home.requestCardTitle')}</strong>
                  <small>{t('home.requestCardTime')}</small>
                </div>
              </header>
              <dl>
                <div><dt>{t('home.requestCardDate')}</dt><dd>{t('home.requestCardDateValue')}</dd></div>
                <div><dt>{t('home.requestCardStart')}</dt><dd>5:00 PM</dd></div>
                <div><dt>{t('home.requestCardEnd')}</dt><dd>10:00 PM</dd></div>
                <div><dt>{t('home.requestCardChildren')}</dt><dd>2</dd></div>
              </dl>
              <div className="request-status">
                <span className="pulse" aria-hidden="true" />
                {t('home.requestCardStatus')}
              </div>
              <p className="request-note">{t('home.requestCardNote')}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="band promises">
        <div className="shell">
          <div className="promise-grid">
            {promises.map((p, i) => (
              <Reveal key={p.title} className="promise" delay={i * 90}>
                <span className="promise-icon"><Icon name={p.icon} /></span>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="band band-tint">
        <div className="shell">
          <div className="feature-grid">
            <Reveal className="feature-media">
              <img src={homeFeature} alt="A nanny drawing with two children at a play table" />
            </Reveal>
            <Reveal className="feature-copy" delay={100}>
              <span className="eyebrow">{t('home.featureEyebrow')}</span>
              <h2>{t('home.featureTitle')}</h2>
              <p>{t('home.featureBody')}</p>
              <Link className="btn btn-secondary" to="/about">{t('home.featureCta')}</Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="band">
        <div className="shell">
          <Reveal className="section-head">
            <span className="eyebrow">{t('home.servicesEyebrow')}</span>
            <h2>{t('home.servicesTitle')}</h2>
            <p className="section-lede">{t('home.servicesLede')}</p>
            <p className="pricing-note">
              <strong>{t('home.pricingNote')}</strong>
            </p>
          </Reveal>

          <div className="service-grid">
            {services.map((s, i) => (
              <Reveal key={s.title} className={`service-card ${s.tag ? 'has-chip' : ''}`} delay={i * 80}>
                {s.tag && <span className="chip">{s.tag}</span>}
                <h3>{s.title}</h3>
                <p>{s.body}</p>
                {s.price && <p className="service-price">{s.price}</p>}
                {s.quote && (
                  <p className="service-price">
                    <Link to="/contact">{t('home.requestQuote')}</Link>
                  </p>
                )}
              </Reveal>
            ))}
          </div>

          <Reveal className="section-foot" delay={120}>
            <Link className="btn btn-secondary" to="/for-families">{t('home.seeHowItWorks')}</Link>
          </Reveal>
        </div>
      </section>

      <section className="band band-tint">
        <div className="shell">
          <Reveal className="section-head">
            <span className="eyebrow">{t('home.stepsEyebrow')}</span>
            <h2>{t('home.stepsTitle')}</h2>
          </Reveal>

          <ol className="steps">
            {steps.map((step, i) => (
              <Reveal as="li" key={step.title} className="step" delay={i * 110}>
                <span className="step-number">{i + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="band">
        <div className="shell">
          <Reveal className="split-cta">
            <article>
              <h2>{t('home.forFamiliesTitle')}</h2>
              <p>{t('home.forFamiliesBody')}</p>
              <Link className="btn btn-primary" to="/register/family">{t('home.createFamilyAccount')}</Link>
            </article>
            <article className="split-cta-alt">
              <h2>{t('home.forNanniesTitle')}</h2>
              <p>{t('home.forNanniesBody')}</p>
              <Link className="btn btn-outline-light" to="/register/nanny">{t('home.startApplication')}</Link>
            </article>
          </Reveal>
        </div>
      </section>
    </>
  );
}
